import {Alert, Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {SurveyInvitation} from 'crqlar-react-common/dist/models/survey/surveyInvitation.model';
import {SurveyService} from 'crqlar-react-common/dist/services/survey/survey.service';
import {getNameForLanguage} from 'crqlar-react-common/dist/utils/translations.util';
import {enqueueSnackbar} from 'notistack';
import {ReactNode, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from 'react-query';
import {getSendGridSnackbarProps} from '../../../utils/reservationNotification.util';
import EmailContentPreview from '../../marketing/automations/components/v2/actions/EmailContentPreview';
import {SHARE_DIALOG_MAX_WIDTH} from './surveyShare.constants';

interface Props {
    invitation: SurveyInvitation
    onClose: () => void
    /** The invitation as it stands after an accepted send. */
    onSent: (invitation: SurveyInvitation) => void
}

/**
 * Confirms emailing a guest their survey link. The mail is fixed (the survey automation designs, German or English
 * by the guest's language) and goes to the guest's primary address only; staff see who gets it and can open the mail
 * itself. A guest who chose not to receive emails is a warning, not a block.
 */
export default function SurveyInvitationEmailDialog({invitation, onClose, onSent}: Props) {
    const {t} = useTranslation();
    const [showMail, setShowMail] = useState(false);
    const [sending, setSending] = useState(false);
    const previewQuery = useQuery(
        ['survey-invitation-email-preview', invitation.id],
        () => SurveyService.invitationEmailPreview({id: invitation.id}),
        {cacheTime: 0, retry: false}
    );
    const preview = previewQuery.data;
    const canSend = !!preview?.to && !sending;

    const send = async () => {
        if (!canSend) return;
        setSending(true);
        try {
            const result = await SurveyService.invitationSendEmail({id: invitation.id});
            const feedback = getSendGridSnackbarProps(t, result.statusCode);
            enqueueSnackbar(feedback.message, {variant: feedback.variant});
            if (result.statusCode >= 200 && result.statusCode < 300) {
                onSent(result.invitation);
                onClose();
            }
        } catch {
            enqueueSnackbar(t('Email notification could not be sent'), {variant: 'error'});
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog
            open
            onClose={sending ? undefined : onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{sx: {maxWidth: SHARE_DIALOG_MAX_WIDTH}}}
        >
            <DialogTitle>{t('Send link by email?')}</DialogTitle>
            <DialogContent>
                {previewQuery.isLoading ? (
                    <Box sx={{display: 'grid', placeItems: 'center', py: 4}}><CircularProgress size={24}/></Box>
                ) : previewQuery.isError || !preview ? (
                    <Alert severity={'error'} action={<Button onClick={() => void previewQuery.refetch()}>{t('Retry')}</Button>}>
                        {t('The email could not be prepared.')}
                    </Alert>
                ) : (
                    <Stack gap={2}>
                        {!preview.to && <Alert severity={'warning'}>{t('This guest has no email address.')}</Alert>}
                        {preview.to && preview.receiveEmails === false && (
                            <Alert severity={'warning'}>{t('This guest chose not to receive emails.')}</Alert>
                        )}
                        <Stack gap={1}>
                            <Row label={t('To')}>{preview.to || '—'}</Row>
                            <Row label={t('Subject')}>{preview.subject}</Row>
                            <Row label={t('Language')}>{getNameForLanguage(preview.language, true) || preview.language}</Row>
                        </Stack>
                        <Box>
                            <Button size={'small'} onClick={() => setShowMail(shown => !shown)}>
                                {t(showMail ? 'Hide email' : 'Show email')}
                            </Button>
                            <Collapse in={showMail} unmountOnExit>
                                <Box sx={{pt: 1}}><EmailContentPreview content={preview.html}/></Box>
                            </Collapse>
                        </Box>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>{t('Cancel')}</Button>
                <LoadingButton variant={'contained'} loading={sending} disabled={!preview?.to} onClick={send}>
                    {t('Send email')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

function Row({label, children}: {label: string, children: ReactNode}) {
    return (
        <Stack direction={'row'} gap={2}>
            <Typography variant={'body2'} color={'text.secondary'} sx={{width: 88, flexShrink: 0}}>{label}</Typography>
            <Typography variant={'body2'} sx={{minWidth: 0, overflowWrap: 'anywhere'}}>{children}</Typography>
        </Stack>
    );
}
